import React, { useMemo, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Search, Filter, X, ExternalLink, ShoppingCart, MapPin, ScrollText, BadgeCheck, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { brands as BRANDS, BRAND_TYPES } from '../data/brandGuide';

const TYPE_PILL_COLOR = {
  Tequila: 'bg-pink-500/20 text-pink-300 border-pink-500/30',
  Mezcal: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  Sotol: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
};

const SOURCE_PILL_COLOR = {
  Verified: 'bg-green-500/15 text-green-300 border-green-500/30',
  'Needs confirmation': 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30',
  'User brand materials; verify before publishing': 'bg-orange-500/15 text-orange-300 border-orange-500/30',
};

const BrandCard = ({ brand, onView }) => {
  const orderUrl = `/order?brand=${encodeURIComponent(brand.brand)}`;
  return (
    <div
      data-testid={`brand-card-${brand.slug}`}
      className="group flex flex-col bg-gradient-to-br from-white/[0.05] to-white/[0.02] backdrop-blur-sm border border-white/10 hover:border-pink-500/40 hover:shadow-2xl hover:shadow-pink-500/10 rounded-2xl p-5 transition-all duration-300 hover:-translate-y-0.5"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <h3 className="text-lg sm:text-xl font-black text-white leading-tight">{brand.brand}</h3>
        <span className={`text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-full border whitespace-nowrap ${TYPE_PILL_COLOR[brand.type] || 'bg-white/10 text-white/70 border-white/15'}`}>
          {brand.type}
        </span>
      </div>

      {/* Expressions chips */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {brand.expressions.slice(0, 5).map((e) => (
          <span key={e} className="text-[11px] sm:text-xs bg-white/5 text-white/75 border border-white/10 px-2 py-0.5 rounded-md">
            {e}
          </span>
        ))}
        {brand.expressions.length > 5 && (
          <span className="text-[11px] sm:text-xs text-white/50 px-1">+{brand.expressions.length - 5} more</span>
        )}
      </div>

      <p className="text-white/65 text-sm leading-relaxed mb-5 flex-grow line-clamp-3">
        {brand.notes}
      </p>

      <div className="flex flex-col sm:flex-row gap-2 mt-auto">
        <Button
          onClick={() => onView(brand)}
          data-testid={`view-brand-${brand.slug}`}
          variant="outline"
          className="flex-1 bg-white/5 border-white/15 text-white hover:bg-white/10 hover:text-white"
        >
          View Brand
        </Button>
        <a
          href={orderUrl}
          data-testid={`order-brand-${brand.slug}`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white font-bold text-sm h-10 px-4 rounded-md transition-all duration-200"
        >
          <ShoppingCart className="w-4 h-4" />
          Order
        </a>
      </div>
    </div>
  );
};

const BrandModal = ({ brand, onClose }) => {
  useEffect(() => {
    if (!brand) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [brand, onClose]);

  if (!brand) return null;
  const orderUrl = `/order?brand=${encodeURIComponent(brand.brand)}`;
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="brand-modal-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto bg-gradient-to-br from-gray-900 via-gray-950 to-gray-900 border border-white/15 rounded-t-3xl sm:rounded-3xl shadow-2xl"
        data-testid="brand-modal"
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 p-5 sm:p-6 bg-gradient-to-b from-gray-900 to-gray-900/80 backdrop-blur-sm border-b border-white/10">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${TYPE_PILL_COLOR[brand.type] || 'bg-white/10 text-white/70 border-white/15'}`}>
                {brand.type}
              </span>
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${SOURCE_PILL_COLOR[brand.source] || 'bg-white/10 text-white/70 border-white/15'}`}>
                <BadgeCheck className="w-3 h-3" /> {brand.source}
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-black text-white">{brand.brand}</h2>
          </div>
          <button
            onClick={onClose}
            data-testid="brand-modal-close"
            className="shrink-0 w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 hover:text-white flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 sm:p-6 space-y-5">
          <section>
            <h4 className="text-white/60 text-xs font-bold uppercase tracking-wider mb-2">Expressions</h4>
            <div className="flex flex-wrap gap-1.5">
              {brand.expressions.map((e) => (
                <span key={e} className="text-xs sm:text-sm bg-white/5 text-white/85 border border-white/15 px-2.5 py-1 rounded-md">
                  {e}
                </span>
              ))}
            </div>
          </section>

          <section>
            <h4 className="text-white/60 text-xs font-bold uppercase tracking-wider mb-2 inline-flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" /> Distillery
            </h4>
            <p className="text-white/85 text-sm sm:text-base leading-relaxed">{brand.distillery}</p>
          </section>

          <section>
            <h4 className="text-white/60 text-xs font-bold uppercase tracking-wider mb-2 inline-flex items-center gap-1.5">
              <ScrollText className="w-3.5 h-3.5" /> Tasting Notes
            </h4>
            <p className="text-white/85 text-sm sm:text-base leading-relaxed whitespace-pre-wrap">{brand.notes}</p>
          </section>

          <a
            href={orderUrl}
            data-testid="brand-modal-order"
            className="mt-4 inline-flex items-center justify-center gap-2 w-full bg-gradient-to-r from-pink-500 via-orange-500 to-yellow-500 hover:from-pink-600 hover:via-orange-600 hover:to-yellow-600 text-white font-black text-base h-12 rounded-xl transition-all duration-200 hover:scale-[1.01] shadow-lg shadow-pink-500/20"
          >
            <ShoppingCart className="w-5 h-5" />
            Order this brand
            <ArrowRight className="w-4 h-4" />
          </a>

          <p className="text-white/45 text-xs leading-relaxed">
            Distillery / NOM and tasting-note data on this page may include placeholders. Confirm by bottle before publishing or sharing externally.
          </p>
        </div>
      </div>
    </div>
  );
};

const BrandGuidePage = () => {
  const [query, setQuery] = useState('');
  const [type, setType] = useState('All');
  const [openBrand, setOpenBrand] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return BRANDS.filter((b) => {
      if (type !== 'All' && b.type !== type) return false;
      if (!q) return true;
      const hay = [
        b.brand,
        b.type,
        b.distillery,
        b.notes,
        ...(b.expressions || [])
      ].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [query, type]);

  const counts = useMemo(() => {
    const acc = { All: BRANDS.length };
    BRANDS.forEach((b) => { acc[b.type] = (acc[b.type] || 0) + 1; });
    return acc;
  }, []);

  return (
    <div className="min-h-screen bg-gray-950 text-white" data-testid="brand-guide-page">
      <Helmet>
        <title>Aloha Tequila Fest Brand Guide & Order Sheet</title>
        <meta name="description" content="Browse 80+ tequila, mezcal and sotol brands at Aloha Tequila & Arts Festival 2026. View distillery info, expressions, tasting notes, and place orders." />
        <link rel="canonical" href="https://www.alohatequilafest.com/brand-guide" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.alohatequilafest.com/' },
            { '@type': 'ListItem', position: 2, name: 'Brand Guide', item: 'https://www.alohatequilafest.com/brand-guide' }
          ]
        })}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative pt-16 sm:pt-20 pb-10 sm:pb-14 px-4 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[50rem] h-[50rem] bg-gradient-radial from-pink-500/15 via-orange-500/5 to-transparent rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-5xl mx-auto text-center">
          <Link to="/" data-testid="brand-guide-back" className="inline-flex items-center gap-1.5 text-white/55 hover:text-white text-sm mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to Festival
          </Link>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-4">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-orange-400 to-yellow-400">
              Aloha Tequila Fest
            </span>
            <span className="block text-white mt-1">Brand Guide</span>
          </h1>
          <p className="text-white/75 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            Click any brand to view distillery details, expressions, tasting notes, and quick order access.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="px-4 mb-8">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
            <Input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by brand, expression, tasting note, or distillery…"
              data-testid="brand-search-input"
              className="pl-9 h-11 bg-white/5 border-white/15 text-white placeholder:text-white/40"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              data-testid="brand-category-select"
              className="pl-9 pr-8 h-11 min-w-[180px] bg-white/5 border border-white/15 text-white rounded-md text-sm appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-pink-500/40"
            >
              {BRAND_TYPES.map((t) => (
                <option key={t} value={t} className="bg-gray-900 text-white">
                  {t} ({counts[t] || 0})
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="max-w-5xl mx-auto mt-3 text-white/55 text-xs sm:text-sm" data-testid="brand-results-count">
          {filtered.length} {filtered.length === 1 ? 'brand' : 'brands'} shown
        </div>
      </section>

      {/* Grid */}
      <section className="px-4 pb-20">
        <div className="max-w-6xl mx-auto">
          {filtered.length === 0 ? (
            <div className="text-center py-16 text-white/55" data-testid="brand-no-results">
              <p className="text-lg mb-2">No brands match your search.</p>
              <button onClick={() => { setQuery(''); setType('All'); }} className="text-pink-300 hover:text-pink-200 text-sm underline">
                Clear filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {filtered.map((b) => (
                <BrandCard key={b.slug} brand={b} onView={setOpenBrand} />
              ))}
            </div>
          )}
        </div>
      </section>

      <BrandModal brand={openBrand} onClose={() => setOpenBrand(null)} />

      {/* Footer back to festival */}
      <div className="border-t border-white/10 py-8 text-center">
        <Link
          to="/"
          data-testid="brand-guide-footer-back"
          className="inline-flex items-center gap-2 text-white/55 hover:text-white text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Aloha Tequila Fest home
          <ExternalLink className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
};

export default BrandGuidePage;
